import { useLazyQuery, useMutation } from "@apollo/client";
import { Box, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import AsyncAutocomplete from "components/Forms/AsyncAutocomplete";
import { IssueListButton } from "components/Forms/IssueListButton";
import {
  FacilityWasteContainersQuery,
  GetWasteContainerQuery,
  Issue,
  IssueLevel,
} from "generated-graphql/graphql";
import { useCallback, useRef, useState } from "react";
import { hasCriticalIssues } from "util/forms";

import {
  CONSOLIDATE_CONTAINERS_MUTATION,
  FACILITY_WASTE_CONTAINERS_QUERY,
  VALIDATE_CONSOLIDATE_CONTAINERS_INPUT,
} from "./api";
import { formatContainerDate, formatQuantity } from "./containerDisplay";

type WasteContainerDetail = NonNullable<GetWasteContainerQuery["wasteContainer"]>;
type SourceOption = NonNullable<
  NonNullable<FacilityWasteContainersQuery["facility"]>["wasteContainers"]
>["items"][number];

/**
 * The date the target will carry after consolidating: the earliest
 * accumulation start among the target and the selected sources — shown up
 * front because "the clock never resets" is the part of consolidation that
 * surprises people.
 */
function inheritedStartDate(target: WasteContainerDetail, sources: SourceOption[]): string | null {
  const dates = [target.accumulationStartDate, ...sources.map((s) => s.accumulationStartDate)]
    .filter((date): date is string => date != null)
    .sort();
  return dates[0] ?? null;
}

export type ConsolidateContainersDialogProps = {
  container: WasteContainerDetail;
  onClose: () => void;
};

export function ConsolidateContainersDialog({
  container,
  onClose,
}: ConsolidateContainersDialogProps) {
  const alerts = useAlerts();
  const [selectedSources, setSelectedSources] = useState<SourceOption[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);

  // Tanks are emptied in place, never consolidated away, and the target
  // can't be its own source — same rules the server enforces. The picker
  // searches server-side, so this only trims the visible page.
  const getSourceOptions = useCallback(
    (data: FacilityWasteContainersQuery) =>
      (data.facility?.wasteContainers?.items ?? []).filter(
        (option) => option.id !== container.id && !option.isPermanent
      ),
    [container.id]
  );

  const [validateQuery] = useLazyQuery(VALIDATE_CONSOLIDATE_CONTAINERS_INPUT, {
    fetchPolicy: "no-cache",
  });
  const validateSelection = useCallback(
    async (sources: SourceOption[]): Promise<Issue[]> => {
      if (sources.length === 0) {
        return [];
      }
      const { data } = await validateQuery({
        variables: {
          input: {
            sourceContainerIds: sources.map((s) => s.id),
            targetContainerId: container.id,
          },
        },
      });
      return data?.consolidateContainersInputValidator ?? [];
    },
    [validateQuery, container.id]
  );

  // Guard against out-of-order validator responses: deselecting resolves
  // instantly while an older in-flight round-trip can land afterwards and
  // resurrect stale issues for a selection that no longer exists.
  const validationSequence = useRef(0);
  const onSelectionChange = async (sources: SourceOption[]) => {
    setSelectedSources(sources);
    const sequence = ++validationSequence.current;
    const nextIssues = await validateSelection(sources);
    if (sequence === validationSequence.current) {
      setIssues(nextIssues);
    }
  };

  const [consolidate, { loading: isSaving }] = useMutation(CONSOLIDATE_CONTAINERS_MUTATION, {
    refetchQueries: [
      "GetWasteContainer",
      "TenantWasteContainers",
      "FacilityWasteContainers",
      "FacilityAccumulationSummary",
    ],
  });

  const onSubmit = async () => {
    if (selectedSources.length === 0) {
      alerts.error("Select at least one container to consolidate");
      return;
    }
    const submitIssues = await validateSelection(selectedSources);
    setIssues(submitIssues);
    if (hasCriticalIssues(submitIssues)) {
      const firstCritical = submitIssues.find((issue) => issue.level === IssueLevel.Critical);
      alerts.error(firstCritical?.message ?? "Fix the critical issues before consolidating");
      return;
    }
    await consolidate({
      variables: {
        input: {
          sourceContainerIds: selectedSources.map((s) => s.id),
          targetContainerId: container.id,
        },
      },
      onCompleted: () => {
        alerts.success("Containers consolidated");
        onClose();
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while consolidating");
      },
    });
  };

  const projectedStartDate = inheritedStartDate(container, selectedSources);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title={`Consolidate into ${container.containerLabel}`}
      primaryActionButtonProps={{
        loading: isSaving,
        onClick: onSubmit,
        disabled: hasCriticalIssues(issues) || selectedSources.length === 0,
        saveText: "Consolidate",
      }}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Each selected container's recorded amount moves into this one and the source is archived.
          The accumulation clock never resets on consolidation — this container inherits the
          earliest start date.
        </Typography>
        <AsyncAutocomplete
          multiple
          query={FACILITY_WASTE_CONTAINERS_QUERY}
          variables={{ facilityId: container.facilityId }}
          getItems={getSourceOptions}
          getOptionLabel={(option: SourceOption) =>
            `${option.containerLabel} — ${formatQuantity(option.amount, option.amountUnit)}`
          }
          value={selectedSources}
          onChange={(sources) => {
            void onSelectionChange(sources ?? []);
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterSelectedOptions
          initialSortModel={[{ field: "containerLabel", sort: "asc" }]}
          fullWidth
          // AsyncAutocomplete labels via placeholderLabel; textFieldProps.label
          // is overridden by it, so the visible label goes here.
          placeholderLabel="Containers to consolidate"
          textFieldProps={{ required: true }}
        />
        {selectedSources.length > 0 && (
          <Box>
            <Typography variant="body2">
              Accumulation start date after consolidating: {formatContainerDate(projectedStartDate)}
            </Typography>
          </Box>
        )}
      </Stack>
    </Dialog>
  );
}
